import { createContext } from 'react'
import type {
  CanvasViewportState,
  CreateTimelineElementInput,
  MarkdownSyncMetadata,
  MockExternalCalendarEvent,
  TimelineElement,
} from '../types/timelineElement'
import type { ChecklistItem } from '../types/schema'
import type { StripboardDelta, StripboardDeltaResult } from '../types/stripboard'
import type {
  DocumentLock,
  TimelineBoardKey,
  WorkspaceMember,
} from '../types/schema'
import type { WorkspaceAction } from '../utils/workspacePermissions'
import { canActOnNode } from '../utils/nodeEditAccess'
import type { Dispatch, SetStateAction } from 'react'

export interface TimelineContextValue {
  elements: TimelineElement[]
  pathOnlyElementIds: Set<string>
  isAccessRestricted?: boolean
  elementsLastSyncedAt: Date | null
  elementsSubscriptionError: Error | null
  refreshElementsSubscription: () => void
  checklistItems: ChecklistItem[]
  canPerformOnNode: (action: WorkspaceAction, nodeId: string | null) => boolean
  workspaceId: string
  memberProfile: WorkspaceMember | null
  locks: DocumentLock[]
  viewportState: CanvasViewportState
  canvasTransform: { x: number; y: number; zoom: number }
  isCinematicModeActive: boolean
  isCalendarShelfOpen: boolean
  externalEventsArray: MockExternalCalendarEvent[]
  markdownMetadata: MarkdownSyncMetadata
  isGestureKnifeActive: boolean
  isPublicReadOnly: boolean
  isLoading: boolean
  checklistsLoaded?: boolean
  error: string | null
  selectedElementId: string | null
  isCanvasMaximized: boolean
  activeCanvasNodeId: string | null
  setViewportState: Dispatch<SetStateAction<CanvasViewportState>>
  setCanvasTransform: (transform: { x: number; y: number; zoom: number }) => void
  setIsCinematicModeActive: (isActive: boolean) => void
  setIsCalendarShelfOpen: (isOpen: boolean) => void
  setExternalEventsArray: Dispatch<SetStateAction<MockExternalCalendarEvent[]>>
  setMarkdownMetadata: Dispatch<SetStateAction<MarkdownSyncMetadata>>
  setIsGestureKnifeActive: (isActive: boolean) => void
  setSelectedElementId: (elementId: string | null) => void
  setIsCanvasMaximized: (isMaximized: boolean) => void
  setActiveCanvasNodeId: (nodeId: string | null) => void
  maximizeCanvasWorkspace: (nodeId?: string) => void
  closeCanvasWorkspace: () => void
  panCanvasToCoordinates: (targetX: number, targetY: number) => void
  calculateElementProgress: (elementId: string) => number
  getExternalLock: (targetId: string) => DocumentLock | null
  verifyUserOperationClearance: (actionType: 'WRITE' | 'AI', actionCost?: number) => boolean
  creditTaskCompletionReward: (itemId: string) => Promise<{ credited: boolean; remaining: number }>
  createElement: (element: CreateTimelineElementInput) => Promise<TimelineElement>
  createElements: (elements: CreateTimelineElementInput[]) => Promise<TimelineElement[]>
  updateElement: (elementId: string, updates: Partial<TimelineElement>) => Promise<void>
  updateChecklistItem: (checklistItemId: string, updates: Partial<ChecklistItem>) => Promise<void>
  applyStripboardDelta: (delta: StripboardDelta) => Promise<StripboardDeltaResult>
  updateChecklistPrerequisites: (checklistItemId: string, prerequisiteIds: string[]) => Promise<void>
  deleteElement: (elementId: string) => Promise<void>
  /** Permanently remove an element + its descendants from Firestore. Used by
   * Manage's Delete button when the selection already lives in Bin. */
  permanentlyDeleteElement: (elementId: string) => Promise<void>
  moveFromBin: (
    elementId: string,
    targetBoard: TimelineBoardKey,
    newParentId: string | null,
  ) => Promise<void>
}

export const TimelineContext = createContext<TimelineContextValue | undefined>(undefined)

export interface ElementQueryPlanInput {
  isPublicReadOnly?: boolean
  appMode?: string | null
  isAdmin?: boolean
  restricted?: boolean
  helper?: boolean
  workspaceId?: string | null
  uid?: string | null
}

export type ElementQueryPlan = 'workspace' | 'legacyReadAccess' | 'scoped' | 'helper'

export const elementQueryPlan = ({
  isPublicReadOnly,
  appMode,
  isAdmin,
  restricted,
  helper,
}: ElementQueryPlanInput): ElementQueryPlan => {
  if (isPublicReadOnly || isAdmin) return 'workspace'
  if (helper) return 'helper'
  if (appMode === 'TEAM') {
    if (restricted) return 'scoped'
    return 'workspace'
  }
  return 'legacyReadAccess'
}

export const mergeScopedElementDocs = <T extends { id: string }>(
  readDocs: T[],
  pathDocs: T[],
): { docs: T[]; pathOnlyIds: Set<string> } => {
  const readDocIds = new Set<string>()
  const docs: T[] = []

  readDocs.forEach((doc) => {
    if (!readDocIds.has(doc.id)) {
      readDocIds.add(doc.id)
      docs.push(doc)
    }
  })

  const pathOnlyIds = new Set<string>()
  pathDocs.forEach((doc) => {
    if (readDocIds.has(doc.id)) {
      return
    }
    if (!pathOnlyIds.has(doc.id)) {
      pathOnlyIds.add(doc.id)
      docs.push(doc)
    }
  })

  return { docs, pathOnlyIds }
}

export interface ScopedElementsCollector<T extends { id: string }> {
  setRead: (docs: T[]) => void
  setPath: (docs: T[]) => void
}

export const createScopedElementsCollector = <T extends { id: string }>(
  onEmit: (result: { docs: T[]; pathOnlyIds: Set<string> }) => void,
): ScopedElementsCollector<T> => {
  let readDocs: T[] | null = null
  let pathDocs: T[] | null = null
  return {
    setRead(docs: T[]) {
      readDocs = docs
      if (readDocs !== null && pathDocs !== null) {
        onEmit(mergeScopedElementDocs(readDocs, pathDocs))
      }
    },
    setPath(docs: T[]) {
      pathDocs = docs
      if (readDocs !== null && pathDocs !== null) {
        onEmit(mergeScopedElementDocs(readDocs, pathDocs))
      }
    },
  }
}

export interface ScopedAccessNode {
  id?: string
  scopeRead?: string[]
  scopeWrite?: string[]
}

export interface ResolveCanPerformOnNodeInput<
  TElement extends ScopedAccessNode = ScopedAccessNode,
  TChecklist extends ScopedAccessNode & { id: string } = ScopedAccessNode & { id: string }
> {
  action: WorkspaceAction
  nodeId: string | null
  canPerform: (action: WorkspaceAction) => boolean
  isAccessRestricted: boolean
  uid?: string | null
  pathOnlyElementIds: Set<string>
  elementById: Map<string, TElement>
  resolvedChecklistItems: TChecklist[]
}

export const resolveCanPerformOnNode = <
  TElement extends ScopedAccessNode = ScopedAccessNode,
  TChecklist extends ScopedAccessNode & { id: string } = ScopedAccessNode & { id: string }
>({
  action,
  nodeId,
  canPerform,
  isAccessRestricted,
  uid,
  pathOnlyElementIds,
  elementById,
  resolvedChecklistItems,
}: ResolveCanPerformOnNodeInput<TElement, TChecklist>): boolean => {
  if (!canPerform(action)) return false
  if (!isAccessRestricted) return true

  if (!nodeId) {
    return canActOnNode({
      action,
      restricted: isAccessRestricted,
      uid,
      node: null,
      isPathOnly: false,
    })
  }

  if (pathOnlyElementIds.has(nodeId)) {
    return false
  }

  const elementNode = elementById.get(nodeId)
  if (elementNode) {
    return canActOnNode({
      action,
      restricted: isAccessRestricted,
      uid,
      node: elementNode,
      isPathOnly: false,
    })
  }

  const checklistNode = resolvedChecklistItems.find((item) => item.id === nodeId)
  if (checklistNode) {
    return canActOnNode({
      action,
      restricted: isAccessRestricted,
      uid,
      node: checklistNode,
      isPathOnly: false,
    })
  }

  // Unknown node while restricted → false.
  return false
}

